import { useEffect, useMemo, useRef, useState } from "react";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { supabaseBrowser } from "@/lib/supabase-browser";
import { track } from "@/lib/analytics/track";
import type { Database } from "@/integrations/supabase/types/database.types";

type CustomerRow = Database["public"]["Tables"]["customers"]["Row"];

export interface KycRealtimeEvent {
  type: "status_changed" | "connection" | "error";
  status?: string | null;
  tosStatus?: string | null;
  raw?: RealtimePostgresChangesPayload<CustomerRow>;
  occurredAt: string;
  message?: string;
}

export interface KycRealtimeState {
  status: string | null;
  tosStatus: string | null;
  lastUpdated: string | null;
  lastEvent: KycRealtimeEvent | null;
  isConnected: boolean;
  isReconnecting: boolean;
  error: string | null;
}

const INITIAL_STATE: KycRealtimeState = {
  status: null,
  tosStatus: null,
  lastUpdated: null,
  lastEvent: null,
  isConnected: false,
  isReconnecting: false,
  error: null,
};

export interface UseKycRealtimeOptions {
  enabled?: boolean;
  debug?: boolean;
  onStatusChange?: (
    nextStatus: string | null,
    payload?: RealtimePostgresChangesPayload<CustomerRow>
  ) => void;
}

/**
 * Hook that subscribes to realtime KYC status updates for the active customer.
 * Acts as the foundation for Variant B (dashboard-driven real-time flow).
 */
export function useKycRealtime(
  customerId: string | null | undefined,
  options: UseKycRealtimeOptions = {}
) {
  const { enabled = true, debug = false, onStatusChange } = options;
  const [state, setState] = useState<KycRealtimeState>(INITIAL_STATE);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const customerKey = customerId ?? null;

  useEffect(() => {
    if (!customerKey || !enabled) {
      setState(INITIAL_STATE);
      return;
    }

    let isMounted = true;
    setState(prev => ({
      ...prev,
      isReconnecting: true,
      error: null,
    }));

    const channelName = `kyc-status:${customerKey}`;
    const channel = supabaseBrowser
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "customers",
          filter: `id=eq.${customerKey}`,
        },
        (payload: RealtimePostgresChangesPayload<CustomerRow>) => {
          if (!isMounted) return;
          const newStatus = payload.new?.kyc_status ?? null;
          const newTosStatus = payload.new?.tos_status ?? null;
          const updatedAt =
            payload.new?.kyc_updated_at ||
            payload.new?.updated_at ||
            new Date().toISOString();

          if (debug) {
             
            console.debug("[useKycRealtime] payload", payload);
          }

          setState(prev => ({
            ...prev,
            status: newStatus ?? prev.status,
            tosStatus: newTosStatus ?? prev.tosStatus,
            lastUpdated: updatedAt,
            lastEvent: {
              type: "status_changed",
              status: newStatus,
              tosStatus: newTosStatus,
              raw: payload,
              occurredAt: new Date().toISOString(),
            },
          }));

          onStatusChange?.(newStatus, payload);
          if (newStatus) {
            track("kyc_realtime_status", {
              status: newStatus,
              tos_status: newTosStatus,
              customer_id: customerKey,
            });
          }
        }
      );

    const handleSubscribeError = (error: { message?: string } | Error | undefined) => {
      if (!isMounted) return;
      setState(prev => ({
        ...prev,
        error: error?.message ?? "Realtime connection failed",
        isConnected: false,
        isReconnecting: false,
        lastEvent: {
          type: "error",
          occurredAt: new Date().toISOString(),
          message: error?.message ?? "Realtime connection failed",
        },
      }));
    };

    const subscribeResult = channel.subscribe(status => {
        if (!isMounted) return;
        if (status === "SUBSCRIBED") {
          setState(prev => ({
            ...prev,
            isConnected: true,
            isReconnecting: false,
            error: null,
            lastEvent: {
              type: "connection",
              occurredAt: new Date().toISOString(),
              message: "Realtime connected",
            },
          }));
        } else if (status === "CHANNEL_ERROR") {
          setState(prev => ({
            ...prev,
            error: "Realtime connection error",
            isConnected: false,
            isReconnecting: true,
            lastEvent: {
              type: "error",
              occurredAt: new Date().toISOString(),
              message: "Realtime connection error",
            },
          }));
        } else if (status === "TIMED_OUT") {
          setState(prev => ({
            ...prev,
            isConnected: false,
            isReconnecting: true,
            error: "Realtime connection timed out",
            lastEvent: {
              type: "error",
              occurredAt: new Date().toISOString(),
              message: "Realtime connection timed out",
            },
          }));
        }
      });

    if (typeof (subscribeResult as PromiseLike<unknown>)?.catch === "function") {
      (subscribeResult as PromiseLike<unknown>).catch(handleSubscribeError);
    }

    channelRef.current = channel;

    return () => {
      isMounted = false;
      if (channelRef.current) {
        supabaseBrowser.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [customerKey, enabled, debug, onStatusChange]);

  return useMemo(() => state, [state]);
}

